import {
  FileUploadState,
  UploadConcurrency,
  UploadContext,
} from "../types/fileUploadWithProgress";
import { FileWithPreview } from "../Chat/types/fileTypes";

import axios from "axios";
import { fileUploadMappingService } from "./fileUploadMappingService";
import { nuclearn_api_url } from "src/App";
import { uploadFileInChunks } from "./unifiedChunkedUploadService";

// Re-export of the global helper for backward compatibility with existing
// imports from this module. New code should import directly from
// ``src/helpers/formatFileSize``.
export { formatFileSize } from "src/helpers/formatFileSize";

/** One row of `GET /datasets/processing-status`. */
interface ProcessingStatusRow {
  source_data_record_id: number;
  filename: string;
  [field: string]: any;
}

/** Identifies one file for a processing-status lookup. */
interface StatusTarget {
  sdrId?: number;
  datasetId?: number;
  filename?: string;
}

/**
 * Picks the row for one file: by SDR id when known, otherwise by exact
 * filename, falling back to the filename without its extension only when no
 * row matches exactly. Returns undefined rather than another file's row,
 * because callers store and later delete the row's SDR id.
 */
export const selectStatusRow = (
  rows: ProcessingStatusRow[],
  { sdrId, filename }: StatusTarget,
): ProcessingStatusRow | undefined => {
  if (sdrId) return rows.find((row) => row.source_data_record_id === sdrId);
  if (!filename) return undefined;
  const stem = filename.replace(/\.[^/.]+$/, "");
  return (
    rows.find((row) => row.filename === filename) ??
    rows.find((row) => row.filename === stem)
  );
};

/** Scopes a processing-status request to the SDR when its id is known. */
const statusQuery = ({ sdrId, datasetId }: StatusTarget) =>
  sdrId ? `sdr_ids=${sdrId}` : `dataset_ids=${datasetId}`;

/**
 * Reads the new file's SDR from a project or dataset upload response.
 *
 * A project upload returns a list of datasets, each with `uploaded_files`; a
 * dataset upload returns `[artifact, sourceDataRecord]`. Returns an empty
 * object for any other shape, including a project response from a backend
 * that predates `uploaded_files`.
 */
export const sdrFromUploadResult = (
  context: UploadContext,
  result: unknown,
  filename: string,
): { sourceDataRecordId?: number; datasetId?: number } => {
  if (!Array.isArray(result)) return {};

  if (context === "project") {
    const uploaded = result.flatMap((dataset) =>
      Array.isArray(dataset?.uploaded_files) ? dataset.uploaded_files : [],
    );
    // One file per chunked request, so a lone entry is this file even when
    // the server cleaned its name.
    const match =
      uploaded.find((f) => f.filename === filename) ??
      (uploaded.length === 1 ? uploaded[0] : undefined);
    return match
      ? {
          sourceDataRecordId: match.source_data_record_id,
          datasetId: match.dataset_id,
        }
      : {};
  }

  if (context === "dataset") {
    const sdr = result[1];
    if (
      result.length === 2 &&
      typeof sdr?.id === "number" &&
      typeof sdr?.dataset_fk === "number" &&
      "source_uid" in sdr
    ) {
      return { sourceDataRecordId: sdr.id, datasetId: sdr.dataset_fk };
    }
  }

  return {};
};

// Upload manager class
export class FileUploadManager {
  private abortControllers: Map<string, AbortController> = new Map();
  private pollingIntervals: Map<string, NodeJS.Timeout> = new Map();
  private activePolling: Set<string> = new Set();

  // Cancel specific file upload/processing
  cancelFile(fileId: string) {
    for (const key of [`upload-${fileId}`, `poll-${fileId}`]) {
      const controller = this.abortControllers.get(key);
      if (controller) {
        controller.abort();
        this.abortControllers.delete(key);
      }
    }

    const interval = this.pollingIntervals.get(fileId);
    if (interval) {
      clearInterval(interval);
      this.pollingIntervals.delete(fileId);
    }

    this.activePolling.delete(fileId);
  }

  // Cancel all operations
  cancelAll() {
    this.abortControllers.forEach((controller) => controller.abort());
    this.abortControllers.clear();

    this.pollingIntervals.forEach((interval) => clearInterval(interval));
    this.pollingIntervals.clear();

    this.activePolling.clear();
  }

  /**
   * Reads one file's processing status once. With no SDR id, the file is
   * found in its dataset by exact filename; no match reports "no status yet".
   */
  async checkProcessingStatus(target: StatusTarget): Promise<{
    isComplete: boolean;
    processingProgress: number;
    numSdr: number;
    numSdrNotEmbedded: number;
    status?: string;
    errorMessage?: string;
    sourceDataRecordId?: number;
  }> {
    try {
      const response = await axios.get<ProcessingStatusRow[]>(
        `${nuclearn_api_url}/datasets/processing-status?${statusQuery(target)}`,
      );

      const statusData = selectStatusRow(response.data ?? [], target);
      if (statusData) {
        const status = statusData.status;
        const progressPercentage = statusData.progress_percentage || 0;
        const numChunks = statusData.num_chunks || 0;
        const numChunksEmbedded = statusData.num_chunks_embedded || 0;
        const sourceDataRecordId = statusData.source_data_record_id;

        // Check if processing is complete
        const isComplete = status === "completed";

        return {
          isComplete,
          processingProgress: progressPercentage,
          numSdr: numChunks,
          numSdrNotEmbedded: numChunks - numChunksEmbedded,
          status,
          errorMessage: statusData.error_message,
          sourceDataRecordId,
        };
      }

      // No status data available yet
      return {
        isComplete: false,
        processingProgress: 0,
        numSdr: 0,
        numSdrNotEmbedded: 0,
      };
    } catch (error) {
      console.error("Error checking processing status:", error);
      return {
        isComplete: false,
        processingProgress: 0,
        numSdr: 0,
        numSdrNotEmbedded: 0,
      };
    }
  }

  // Upload a single file
  async uploadFile(
    context: UploadContext,
    contextId: string | number,
    fileState: FileUploadState,
    onProgress: (fileState: FileUploadState) => void,
    onChunkProgress?: (
      chunkIndex: number,
      isUploaded: boolean,
      error?: string,
    ) => void,
    chunkSize?: number,
  ): Promise<FileUploadState> {
    const controller = new AbortController();
    this.abortControllers.set(`upload-${fileState.id}`, controller);

    try {
      const updatedState = {
        ...fileState,
        uploadStatus: "uploading" as const,
      };
      onProgress(updatedState);

      const response = await uploadFileInChunks({
        context,
        contextId: String(contextId),
        file: fileState.file,
        chunkSize,
        onProgress: (progress) => {
          const progressState = {
            ...updatedState,
            uploadProgress: progress,
          };
          onProgress(progressState);
        },
        onChunkProgress: (chunkIndex, isUploaded, error) => {
          onChunkProgress?.(chunkIndex, isUploaded, error);
        },
        onTotalChunks: (totalChunks) => {
          updatedState.totalChunks = totalChunks;
        },
        onUploadIdReceived: (uploadId) => {
          updatedState.uploadId = uploadId;
          console.log(
            `📋 UPLOAD ID RECEIVED: File "${fileState.file.name}" assigned upload_id: ${uploadId}`,
          );
        },
      });

      console.log(
        `Upload complete: File "${fileState.file.name}" finished chunked upload. Response:`,
        response,
      );

      // --- Dialogue context: dataset + workspace upload ---
      // Backend returns DialogueUploadResponse with dataset info and per-file
      // artifact/SDR IDs.  If any of these are missing, the file cannot be used
      // downstream, so we throw to let the caller surface an error toast.
      if (context === "dialogue") {
        const dialogueResponse = response.result ?? response;

        // Check for per-file errors in the partial-success response
        if (dialogueResponse?.errors?.length > 0) {
          const fileError =
            dialogueResponse.errors.find(
              (e: any) =>
                e.filename === updatedState.file.name ||
                e.filename === (updatedState.file as any).originalName,
            ) ?? dialogueResponse.errors[0];
          throw new Error(fileError.error);
        }

        if (
          !dialogueResponse?.dataset_id ||
          !dialogueResponse?.uploaded_files?.length
        ) {
          throw new Error(
            `Upload succeeded for "${updatedState.file.name}" but the server response is incomplete. Please try again or contact support.`,
          );
        }

        const dialogueDatasetId: number = dialogueResponse.dataset_id;
        const uploadedFile =
          dialogueResponse.uploaded_files.find(
            (f: any) =>
              f.filename === updatedState.file.name ||
              f.filename === (updatedState.file as any).originalName,
          ) ?? dialogueResponse.uploaded_files[0];

        if (
          !uploadedFile?.artifact_id ||
          !uploadedFile?.source_data_record_id
        ) {
          throw new Error(
            `Upload succeeded for "${updatedState.file.name}" but required file metadata is incomplete. Please try again or contact support.`,
          );
        }

        const artifactId: number = uploadedFile.artifact_id;
        const sourceDataRecordId: number = uploadedFile.source_data_record_id;

        const completedState: FileUploadState = {
          ...updatedState,
          uploadStatus: "uploaded" as const,
          uploadProgress: 100,
          datasetId: dialogueDatasetId,
          sourceDataRecordId,
          artifactId,
          origin: "dialogue" as const,
          // Upload is done; embedding continues. Set processingStatus here so
          // the state is self-consistent with uploadStatus and derived
          // readiness doesn't hinge on the first poll landing. The poll flips
          // this to "completed" when indexing finishes.
          processingStatus: "processing" as const,
        };

        (completedState.file as FileWithPreview).datasetId = dialogueDatasetId;
        (completedState.file as FileWithPreview).sourceDataRecordId =
          sourceDataRecordId;

        console.log(
          `Dialogue upload complete: "${completedState.file.name}" -> dataset ${dialogueDatasetId}, artifact ${artifactId}, sdr ${sourceDataRecordId}`,
        );

        onProgress(completedState);
        return completedState;
      }

      // --- Project / Dataset context: extract dataset IDs ---
      const uploaded = sdrFromUploadResult(
        context,
        response.result,
        fileState.file.name,
      );
      let datasetId: number | undefined = uploaded.datasetId;
      let sourceDataRecordId: number | undefined = uploaded.sourceDataRecordId;
      let serverFilename: string | undefined;

      if (response.result && !sourceDataRecordId) {
        // Special handling for project uploads which might have different response structure
        if (context === "project" && response.result) {
          // Project uploads might return a project object with dataset_folder
          if (response.result.dataset_folder?.datasets) {
            const datasets = response.result.dataset_folder.datasets;

            // Find the newly created dataset - usually the last one or matching by filename
            const newDataset =
              datasets.find((d: any) => {
                const nameMatch =
                  d.name === fileState.file.name ||
                  d.name === fileState.file.name.replace(/\.[^/.]+$/, "");
                const sdrMatch = d.source_data_records?.some(
                  (sdr: any) =>
                    sdr.data?.file_name === fileState.file.name ||
                    sdr.data?.filename === fileState.file.name,
                );
                return nameMatch || sdrMatch;
              }) || datasets[datasets.length - 1];

            if (newDataset) {
              datasetId = newDataset.id;
              serverFilename = newDataset.name;

              if (newDataset.source_data_records?.length > 0) {
                sourceDataRecordId = newDataset.source_data_records[0].id;
              }
            }
          }
        }

        // If not handled by project-specific logic, use standard extraction
        if (!datasetId && !updatedState.uploadId) {
          let datasets: any[] = [];
          if (Array.isArray(response.result)) {
            datasets = response.result;
          } else if (response.result.dataset_folder?.datasets?.length > 0) {
            datasets = response.result.dataset_folder.datasets;
          } else if (response.result.id && response.result.name) {
            datasets = [response.result];
          }

          if (datasets.length > 0) {
            const dataset = datasets[0];
            datasetId = dataset.id;
            serverFilename = dataset.name;
            if (dataset.source_data_records?.length > 0) {
              sourceDataRecordId = dataset.source_data_records[0].id;
            }
          }
        } else if (!datasetId) {
          // Multi-chunk upload - use mapping service
          if (response.result) {
            const mappingResult =
              fileUploadMappingService.findDatasetFromUploadResult(
                fileState.file.name,
                response.result,
              );

            if (mappingResult) {
              datasetId = mappingResult.datasetId;
              serverFilename = mappingResult.serverFilename;

              if (updatedState.uploadId) {
                fileUploadMappingService.updateServerFilename(
                  updatedState.uploadId,
                  serverFilename,
                  datasetId,
                );
              }

              let datasets: any[] = [];
              if (Array.isArray(response.result)) {
                datasets = response.result;
              } else if (response.result.dataset_folder?.datasets?.length > 0) {
                datasets = response.result.dataset_folder.datasets;
              }

              const dataset = datasets.find((d) => d.id === datasetId);
              if (dataset?.source_data_records?.length > 0) {
                sourceDataRecordId = dataset.source_data_records[0].id;
              }
            }
          }
        }
      }

      const completedState = {
        ...updatedState,
        uploadStatus: "uploaded" as const,
        uploadProgress: 100,
        datasetId,
        sourceDataRecordId,
        origin: (context === "project" ? "project" : "dataset") as
          | "project"
          | "dataset",
      };

      if (datasetId) {
        (completedState.file as FileWithPreview).datasetId = datasetId;
      }
      if (sourceDataRecordId) {
        (completedState.file as FileWithPreview).sourceDataRecordId =
          sourceDataRecordId;
      }

      console.log(
        `Upload complete: "${completedState.file.name}" | Dataset: ${datasetId || "N/A"} | SDR: ${sourceDataRecordId || "N/A"} | Context: ${context}`,
      );

      // Log warning if project upload doesn't have SDR ID
      if (!sourceDataRecordId && context === "project") {
        console.warn(
          `⚠️ PROJECT UPLOAD WARNING: No SDR ID found immediately for file "${
            completedState.file.name
          }".
          Dataset ID: ${datasetId || "not found"}
          Response structure:`,
          response.result,
        );
      }

      // If we don't have SDR ID yet but have dataset ID, fetch it from processing status
      if (!sourceDataRecordId && datasetId) {
        try {
          const statusResult = await this.checkProcessingStatus({
            datasetId,
            filename: completedState.file.name,
          });
          if (statusResult.sourceDataRecordId) {
            completedState.sourceDataRecordId = statusResult.sourceDataRecordId;
            (completedState.file as FileWithPreview).sourceDataRecordId =
              statusResult.sourceDataRecordId;
          }
        } catch (error) {
          console.error(
            "Failed to fetch SDR ID from processing status:",
            error,
          );
        }
      }

      onProgress(completedState);

      return completedState;
    } catch (error) {
      let errorMessage =
        error instanceof Error ? error.message : "Upload failed";

      // Check if it's a conflict error (409)
      if (
        (error as any)?.isConflictError ||
        (error as any)?.statusCode === 409
      ) {
        errorMessage =
          "Conflict: The file already exists or another upload is in progress. Please try with a different file or wait and try again.";
        console.error(
          `❌ Conflict error (409) during upload of ${fileState.file.name}. Upload will not be retried.`,
        );
      }

      const errorState = {
        ...fileState,
        uploadStatus: "failed" as const,
        error: errorMessage,
      };
      onProgress(errorState);
      throw error;
    } finally {
      this.abortControllers.delete(`upload-${fileState.id}`);
    }
  }

  // Poll processing status for a file with improved reliability
  async pollProcessingStatus(
    fileState: FileUploadState,
    onProgress: (fileState: FileUploadState) => void,
    onComplete: (fileState: FileUploadState) => void,
    pollInterval: number = 2000,
    maxAttempts: number = Infinity, // Default to infinite polling
    minProcessingDisplayTime: number = 3000, // Minimum time to show processing stage (3 seconds)
  ): Promise<void> {
    if (!fileState.datasetId && !fileState.sourceDataRecordId) {
      throw new Error(
        "No dataset ID or SDR ID available for processing status check",
      );
    }

    // Prevent duplicate polling for the same file
    if (this.activePolling.has(fileState.id)) {
      console.log(`Polling already active for file ${fileState.id}, skipping`);
      return;
    }

    // Check if already completed
    if (fileState.processingStatus === "completed") {
      console.log(`File ${fileState.id} already completed, skipping polling`);
      return;
    }

    this.activePolling.add(fileState.id);
    const controller = new AbortController();
    this.abortControllers.set(`poll-${fileState.id}`, controller);

    let attempts = 0;
    let isProcessingComplete = false;
    let waitingForInitialSDR = true; // Track if we're still waiting for initial SDR creation
    let consecutiveZeroSdrCount = 0; // Track consecutive zero SDR responses
    const processingStartTime = Date.now(); // Track when processing display started

    const updatedState = {
      ...fileState,
      processingStatus: "processing" as const,
      processingProgress: 0,
    };
    onProgress(updatedState);

    const checkStatus = async () => {
      try {
        // Re-read each attempt: a filename match below records the SDR id, and
        // later polls then query that record alone.
        const target: StatusTarget = {
          sdrId: updatedState.sourceDataRecordId,
          datasetId: fileState.datasetId,
          filename: fileState.file.name,
        };
        const response = await axios.get<ProcessingStatusRow[]>(
          `${nuclearn_api_url}/datasets/processing-status?${statusQuery(target)}`,
          { signal: controller.signal },
        );

        const statusData = selectStatusRow(response.data ?? [], target);
        if (!statusData) {
          consecutiveZeroSdrCount++;
          console.log(
            `Poll attempt ${attempts + 1} for file ${fileState.file.name} (${
              fileState.datasetId
            }): No status data yet`,
          );

          // Show progress as "preparing" while waiting for status data
          const progressState = {
            ...updatedState,
            processingProgress: Math.min(5, attempts * 0.5),
            numSdr: 0,
            numSdrNotEmbedded: 0,
          };
          onProgress(progressState);
          return false;
        }

        const status = statusData.status;
        const progressPercentage = statusData.progress_percentage || 0;
        const numChunks = statusData.num_chunks || 0;
        const numChunksEmbedded = statusData.num_chunks_embedded || 0;
        const sourceDataRecordId = statusData.source_data_record_id;

        // Get backend-provided display fields
        const displayStatus = statusData.display_status;
        const isProcessing = statusData.is_processing;

        console.log(
          `Poll attempt ${attempts + 1} for file ${fileState.file.name} (${
            fileState.datasetId
          }): Status=${status}, Display=${displayStatus}, Processing=${isProcessing}, Progress=${progressPercentage}%, Chunks=${numChunks}, Embedded=${numChunksEmbedded}, SDR=${sourceDataRecordId}`,
        );

        // Handle case where chunks haven't been created yet (e.g., still in OCR/conversion)
        if (numChunks === 0 && status !== "completed" && status !== "failed") {
          consecutiveZeroSdrCount++;

          // If we've been waiting too long, log warning
          if (consecutiveZeroSdrCount > 30) {
            console.warn(
              `File ${fileState.file.name} has no chunks after ${consecutiveZeroSdrCount} attempts. Status: ${status}`,
            );
          }

          // Show progress based on status even without chunks
          const progressState = {
            ...updatedState,
            // Backend-provided fields
            display_status: displayStatus,
            is_processing: isProcessing,
            progress_percentage: progressPercentage,
            status: status,
            // Legacy fields
            processingProgress: progressPercentage,
            processingStage: status,
            numSdr: numChunks,
            numSdrNotEmbedded: numChunks - numChunksEmbedded,
          };
          onProgress(progressState);
          return false; // Not complete yet
        } else {
          // Chunks have been created or processing is complete, reset counters
          consecutiveZeroSdrCount = 0;
          waitingForInitialSDR = false;
        }

        // Use the progress percentage from backend
        const progressState = {
          ...updatedState,
          // Backend-provided fields
          display_status: displayStatus,
          is_processing: isProcessing,
          progress_percentage: progressPercentage,
          status: status,
          // Legacy fields
          processingProgress: progressPercentage,
          processingStage: status,
          numSdr: numChunks,
          numSdrNotEmbedded: numChunks - numChunksEmbedded,
        };

        // If we got SDR ID from processing status and don't have it yet, update it
        if (sourceDataRecordId && !updatedState.sourceDataRecordId) {
          updatedState.sourceDataRecordId = sourceDataRecordId;
          progressState.sourceDataRecordId = sourceDataRecordId;
          (progressState.file as any).sourceDataRecordId = sourceDataRecordId;

          console.log(
            `🔍 SDR-FROM-POLLING: File "${fileState.file.name}" | SDR ID: ${sourceDataRecordId} | Dataset ID: ${fileState.datasetId}`,
          );
        } else if (updatedState.sourceDataRecordId) {
          // Preserve existing SDR ID
          progressState.sourceDataRecordId = updatedState.sourceDataRecordId;
          (progressState.file as any).sourceDataRecordId =
            updatedState.sourceDataRecordId;
        }

        // Ensure datasetId is on the file object
        if (fileState.datasetId) {
          (progressState.file as any).datasetId = fileState.datasetId;
        }

        onProgress(progressState);

        // Check if processing is complete based on status
        const isComplete = status === "completed";

        if (isComplete) {
          // Check if minimum processing display time has passed
          const processingDisplayTime = Date.now() - processingStartTime;
          if (processingDisplayTime < minProcessingDisplayTime) {
            // Still need to wait for minimum display time
            console.log(
              `⏳ File ${
                fileState.file.name
              } processing complete but waiting for minimum display time (${
                minProcessingDisplayTime - processingDisplayTime
              }ms remaining)`,
            );
            // Continue showing processing progress but don't mark as complete yet
            return false;
          }

          isProcessingComplete = true;
          const completeState = {
            ...progressState,
            processingStatus: "completed" as const,
            processingProgress: 100,
          };
          console.log(
            `✅ File ${fileState.file.name} processing completed! Status: ${status}, Chunks: ${numChunks}`,
          );
          onProgress(completeState);
          onComplete(completeState);
        } else if (status === "failed") {
          // Handle failed status
          throw new Error(statusData.error_message || "Processing failed");
        }

        return isProcessingComplete;
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") {
          throw error;
        }
        console.error(
          `Error checking processing status for file ${fileState.file.name}:`,
          error,
        );

        // For API errors, wait a bit longer before next attempt
        await new Promise((resolve) => setTimeout(resolve, 1000));
        return false;
      }
    };

    try {
      console.log(
        `🔄 Starting polling for file ${fileState.file.name} (datasetId: ${fileState.datasetId})`,
      );

      // Initial delay to allow file processing to start
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Start polling
      while (!isProcessingComplete && attempts < maxAttempts) {
        if (controller.signal.aborted) {
          console.log(`Polling aborted for file ${fileState.id}`);
          break;
        }

        isProcessingComplete = await checkStatus();
        attempts++;

        if (!isProcessingComplete) {
          // Adaptive polling interval - longer intervals if waiting for initial SDR creation
          const currentInterval =
            waitingForInitialSDR && attempts > 10
              ? pollInterval * 1.5 // Slower polling while waiting for SDRs
              : pollInterval;

          await new Promise((resolve) => setTimeout(resolve, currentInterval));
        } else {
          console.log(
            `✅ Polling completed for file ${fileState.file.name} after ${attempts} attempts`,
          );
          break;
        }
      }

      if (
        !isProcessingComplete &&
        attempts >= maxAttempts &&
        maxAttempts !== Infinity
      ) {
        console.error(
          `⏰ Processing timeout for file ${fileState.file.name} after ${maxAttempts} attempts`,
        );

        // Mark as failed instead of throwing error to allow other files to continue
        const failedState = {
          ...updatedState,
          processingStatus: "failed" as const,
          error: `Processing timeout after ${maxAttempts} attempts`,
        };
        onProgress(failedState);
      } else if (!isProcessingComplete && maxAttempts === Infinity) {
        // For infinite polling, just log that polling continues
        console.log(
          `♾️ Continuing infinite polling for file ${fileState.file.name} (attempt ${attempts})`,
        );
      }
    } finally {
      // Always clean up
      this.abortControllers.delete(`poll-${fileState.id}`);
      this.activePolling.delete(fileState.id);
      // Polling cleanup complete
    }
  }

  // Upload multiple files with concurrency control
  async uploadMultipleFiles(
    context: UploadContext,
    contextId: string | number,
    fileStates: FileUploadState[],
    concurrency: UploadConcurrency,
    onFileProgress: (fileState: FileUploadState) => void,
    onFileComplete: (fileState: FileUploadState) => void,
    onFileError: (fileState: FileUploadState, error: string) => void,
    chunkSize?: number,
  ): Promise<FileUploadState[]> {
    if (concurrency === "sequential") {
      return this.uploadSequentially(
        context,
        contextId,
        fileStates,
        onFileProgress,
        onFileComplete,
        onFileError,
        chunkSize,
      );
    } else {
      return this.uploadInParallel(
        context,
        contextId,
        fileStates,
        onFileProgress,
        onFileComplete,
        onFileError,
        chunkSize,
      );
    }
  }

  // Sequential upload
  private async uploadSequentially(
    context: UploadContext,
    contextId: string | number,
    fileStates: FileUploadState[],
    onFileProgress: (fileState: FileUploadState) => void,
    onFileComplete: (fileState: FileUploadState) => void,
    onFileError: (fileState: FileUploadState, error: string) => void,
    chunkSize?: number,
  ): Promise<FileUploadState[]> {
    const results: FileUploadState[] = [];

    // Mark all files except the first as queued (pending) initially
    fileStates.forEach((fileState, index) => {
      if (index > 0) {
        // Keep files after the first one in pending state
        console.log(
          `📋 Marking file ${fileState.file.name} as queued (pending)`,
        );
        onFileProgress({
          ...fileState,
          uploadStatus: "pending",
          uploadProgress: 0,
        });
      }
    });

    for (const fileState of fileStates) {
      try {
        // Upload file
        const uploadedState = await this.uploadFile(
          context,
          contextId,
          fileState,
          onFileProgress,
          undefined,
          chunkSize,
        );

        // Poll processing status for all upload contexts (including dialogue)
        // so that processingStatus transitions to "completed" when done.
        if (uploadedState.sourceDataRecordId || uploadedState.datasetId) {
          this.pollProcessingStatus(
            uploadedState,
            onFileProgress,
            onFileComplete,
          ).catch((error) => {
            const errorMessage =
              error instanceof Error ? error.message : "Processing failed";
            onFileError(
              { ...uploadedState, error: errorMessage },
              errorMessage,
            );
          });
        } else {
          onFileError(
            uploadedState,
            "No dataset ID or SDR ID found - processing status cannot be tracked",
          );
        }

        results.push(uploadedState);
      } catch (error) {
        let errorMessage =
          error instanceof Error ? error.message : "Upload failed";

        // Check if it's a conflict error and provide clearer message
        if (
          (error as any)?.isConflictError ||
          (error as any)?.statusCode === 409
        ) {
          errorMessage =
            "Conflict: The file already exists or another upload is in progress. Please try with a different file or wait and try again.";

          // For conflict errors, don't add to results and stop immediately
          console.error(
            `❌ 409 Conflict error - stopping sequential upload queue`,
          );
          const errorState = {
            ...fileState,
            error: errorMessage,
            uploadStatus: "failed" as const,
          };
          onFileError(errorState, errorMessage);

          // Don't continue with remaining files on conflict
          break;
        }

        const errorState = { ...fileState, error: errorMessage };
        onFileError(errorState, errorMessage);
        results.push(errorState);
      }
    }

    return results;
  }

  // Parallel upload
  private async uploadInParallel(
    context: UploadContext,
    contextId: string | number,
    fileStates: FileUploadState[],
    onFileProgress: (fileState: FileUploadState) => void,
    onFileComplete: (fileState: FileUploadState) => void,
    onFileError: (fileState: FileUploadState, error: string) => void,
    chunkSize?: number,
  ): Promise<FileUploadState[]> {
    const uploadPromises = fileStates.map(async (fileState) => {
      try {
        // Upload file
        const uploadedState = await this.uploadFile(
          context,
          contextId,
          fileState,
          onFileProgress,
          undefined,
          chunkSize,
        );

        // Poll processing status for all upload contexts (including dialogue)
        if (uploadedState.sourceDataRecordId || uploadedState.datasetId) {
          this.pollProcessingStatus(
            uploadedState,
            onFileProgress,
            onFileComplete,
          ).catch((error) => {
            const errorMessage =
              error instanceof Error ? error.message : "Processing failed";
            onFileError(
              { ...uploadedState, error: errorMessage },
              errorMessage,
            );
          });
        } else {
          onFileError(
            uploadedState,
            "No dataset ID or SDR ID found - processing status cannot be tracked",
          );
        }

        return uploadedState;
      } catch (error) {
        let errorMessage =
          error instanceof Error ? error.message : "Upload failed";

        // Check if it's a conflict error and provide clearer message
        if (
          (error as any)?.isConflictError ||
          (error as any)?.statusCode === 409
        ) {
          errorMessage =
            "Conflict: The file already exists or another upload is in progress. Please try with a different file or wait and try again.";

          // For conflict errors, mark as failed and don't add to results
          console.error(
            `❌ 409 Conflict error in parallel upload for file: ${fileState.file.name}`,
          );
          const errorState = {
            ...fileState,
            error: errorMessage,
            uploadStatus: "failed" as const,
          };
          onFileError(errorState, errorMessage);

          // Return null for conflict errors to filter them out
          return null;
        }

        const errorState = { ...fileState, error: errorMessage };
        onFileError(errorState, errorMessage);
        return errorState;
      }
    });

    // Filter out null results (conflict errors) and return only successful/failed uploads
    const results = await Promise.all(uploadPromises);
    return results.filter(
      (result): result is FileUploadState => result !== null,
    );
  }
}
